import type * as FavCache from '@finn-no/favorite-api-browser-client';
import { ItemType } from '@finn-no/favorite-api-browser-client';
import { TrackerData } from '@finn-no/pulse-sdk/dist/types';
import pulse from '@finn-no/pulse-sdk';

export type Result<T, E = Error> =
    | { ok: true; value: T }
    | { ok: false; error: E };

export class CreateFolderError extends Error {
    public readonly isDueToConflict: boolean;

    constructor(message: string, isDueToConflict: boolean) {
        super(message);
        this.name = 'CreateFolderError';
        this.isDueToConflict = isDueToConflict;
    }
}

export class AddFavoriteError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'AddFavoriteError';
    }
}

class RemoveFavoriteError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'RemoveFavoriteError';
    }
}

export type ApiCallFavoriteProps = {
    api: FavCache.FavoriteBrowserCache;
    folderId: number;
    itemId: number;
    itemType: ItemType;
};

export type ApiCallFolderProps = {
    api: FavCache.FavoriteBrowserCache;
    folderName: string;
};

export const markAsFavorite = async (
    props: ApiCallFavoriteProps,
): Promise<Result<void, AddFavoriteError>> => {
    const { api, folderId, itemId, itemType } = props;
    try {
        const response = await api.addToFolder(itemType, itemId, folderId);
        if (response.status !== 201) {
            return {
                ok: false,
                error: new AddFavoriteError(
                    `Could not add item ${itemId} to folder ${folderId}. Status code: ${response.status}.`,
                ),
            };
        }
        const pulsePayload: TrackerData = {
            type: 'Click',
            intent: 'Save',
            name: 'New favorite',
            object: {
                id: `favoritelist:${folderId}`,
                type: 'UIElement',
                elementType: 'Popup',
                name: 'Favorite List',
                items: [
                    {
                        type: 'ClassifiedAd',
                        id:
                            itemType === 'Hotel'
                                ? `hotel:${itemId}`
                                : `${itemId}`,
                        contentId: `${itemId}`,
                        name: 'n/a',
                        category: 'n/a',
                    },
                ],
            },
            target: {
                id:
                    itemType === 'Hotel'
                        ? `classified:hotel:${itemId}`
                        : `classified:${itemId}`,
                type: 'SavedList',
                name: 'Favorite list',
            },
        };
        pulse.trackEvent(pulsePayload);
        return { ok: true, value: undefined };
    } catch (_e: unknown) {
        return {
            ok: false,
            error: new AddFavoriteError(`Unknown error occurred`),
        };
    }
};

export const removeFavorite = async (
    props: ApiCallFavoriteProps,
): Promise<Result<void, RemoveFavoriteError>> => {
    const { api, folderId, itemId, itemType } = props;
    try {
        const response = await api.removeFromFolder(itemType, itemId, folderId);
        if (response.status !== 204) {
            return {
                ok: false,
                error: new RemoveFavoriteError(
                    `Could not remove item ${itemId} from folder ${folderId}. Status code: ${response.status}.`,
                ),
            };
        }
        const pulsePayload: TrackerData = {
            type: 'Click',
            intent: 'Edit',
            name: 'New favorite',
            object: {
                id: `favoritelist:${folderId}`,
                type: 'UIElement',
                elementType: 'Button',
                name: 'Favorite List',
                items: [
                    {
                        type: 'ClassifiedAd',
                        id:
                            itemType === 'Hotel'
                                ? `hotel:${itemId}`
                                : `${itemId}`,
                        contentId: `${itemId}`,
                        name: 'n/a',
                        category: 'n/a',
                    },
                ],
            },
            target: {
                id:
                    itemType === 'Hotel'
                        ? `classified:hotel:${itemId}`
                        : `classified:${itemId}`,
                type: 'FavoriteItem',
                name: 'Classified Ad',
            },
        };
        pulse.trackEvent(pulsePayload);
        return { ok: true, value: undefined };
    } catch (_e: unknown) {
        return {
            ok: false,
            error: new RemoveFavoriteError(`Unknown error occurred`),
        };
    }
};

export const createFolder = async (
    props: ApiCallFolderProps,
): Promise<Result<number, CreateFolderError>> => {
    const { api, folderName } = props;
    try {
        const res = await api.createFolder(folderName);
        if (!res.ok) {
            return {
                ok: false,
                error: new CreateFolderError(
                    `${res.status}`,
                    res.status === 409,
                ),
            };
        }
        const { folderid: folderId } = await res.json();
        const pulsePayload: TrackerData = {
            type: 'Click',
            intent: 'Create',
            name: 'New favorite list',
            object: {
                type: 'UIElement',
                id: `favoritelist:${folderId}`,
                elementType: 'Popup',
            },
            target: {
                id: `favoritelist:${folderId}`,
                type: 'SavedList',
                name: 'Favorite list',
            },
        };
        pulse.trackEvent(pulsePayload);
        return {
            ok: true,
            value: folderId,
        };
    } catch (_e: unknown) {
        return {
            ok: false,
            error: new CreateFolderError(`Unknown error occurred`, false),
        };
    }
};
